import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';

const switchVariants = cva(
  'peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-none transition-colors focus-ring',
  {
    variants: {
      size: {
        standard: 'h-5 w-9',
        small: 'h-4 w-6',
      },
      disabled: {
        true: 'cursor-not-allowed bg-grey-30 data-[state=checked]:bg-grey-30',
        false: 'bg-grey-50 data-[state=checked]:bg-primary',
      },
    },
    defaultVariants: {
      size: 'standard',
      disabled: false,
    },
  },
);

const switchThumbVariants = cva(
  'pointer-events-none block rounded-full bg-white shadow-sm transition-transform duration-100 will-change-transform',
  {
    variants: {
      size: {
        standard: 'h-[18px] w-[18px] translate-x-px data-[state=checked]:translate-x-[17px]',
        small: 'h-3 w-3 translate-x-[2px] data-[state=checked]:translate-x-[10px]',
      },
    },
    defaultVariants: {
      size: 'standard',
    },
  },
);

type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> & VariantProps<typeof switchVariants>;

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, size, disabled, ...props }, ref) => {
    // Override disabled variant when disabled prop is true
    const effectiveDisabled = disabled ?? false;

    return (
      <SwitchPrimitive.Root
        ref={ref}
        data-slot="switch"
        className={cn(switchVariants({ size, disabled: effectiveDisabled }), className)}
        disabled={disabled}
        {...props}
      >
        <SwitchPrimitive.Thumb data-slot="switch-thumb" className={cn(switchThumbVariants({ size }))} />
      </SwitchPrimitive.Root>
    );
  },
);

Switch.displayName = 'Switch';

export { Switch, switchVariants, switchThumbVariants };
export type { SwitchProps };
